import { ComponentProps, FC, useId } from "react";
import { Slot } from "@radix-ui/react-slot";
import {
  Controller,
  FormProvider,
  UseFormReturn,
  type ControllerProps,
  type FieldPath,
  type FieldValues,
  type SubmitHandler,
} from "react-hook-form";

import { I18nKeyType } from "@libs/i18n";
import { RmLabel } from "@/components/RmLabel";
import { FormFieldContext } from "@/context/FormFieldProvider";
import { FormItemContext } from "@/context/FormItemProvider";
import { useFormField } from "@/hooks/useFormField";
import { useTranslate } from "@/hooks/useTranslate";
import { ChildrenOrI18nType } from "@/utils/react";
import { cn } from "@/utils/tailwind";

import { textVariants } from "./RmText/variants";

type RmFormSubmitValuesType<
  T extends FieldValues,
  TTransformedValues extends FieldValues | undefined,
> = TTransformedValues extends FieldValues ? TTransformedValues : T;

type RmFormPropsType<
  T extends FieldValues,
  TContext = unknown,
  TTransformedValues extends FieldValues | undefined = undefined,
> = Omit<ComponentProps<"form">, "onSubmit"> & {
  form: UseFormReturn<T, TContext, TTransformedValues>;
  className?: string;
  isInputOnlyForm?: boolean;
  onSubmit?: (values: RmFormSubmitValuesType<T, TTransformedValues>) => void;
} & (
    | {
        isInputOnlyForm: true;
        onSubmit?: never;
      }
    | {
        isInputOnlyForm?: false;
        onSubmit: (
          values: RmFormSubmitValuesType<T, TTransformedValues>,
        ) => void;
      }
  );

export const RmForm = <
  T extends FieldValues,
  TContext = unknown,
  TTransformedValues extends FieldValues | undefined = undefined,
>({
  form,
  children,
  onSubmit,
  className,
  isInputOnlyForm = false,
  ...props
}: RmFormPropsType<T, TContext, TTransformedValues>) => {
  if (isInputOnlyForm) {
    return <FormProvider {...form}>{children}</FormProvider>;
  }

  return (
    <FormProvider {...form}>
      <form
        className={cn("flex w-full flex-col gap-6", className)}
        onSubmit={event => {
          if (onSubmit) {
            // `handleSubmit` returns a conditional type that TypeScript cannot
            // reduce while `TTransformedValues` is still an unresolved generic.
            // Narrowing through a typed alias makes the call site type-safe.
            const handleSubmit = form.handleSubmit as (
              onValid: SubmitHandler<
                RmFormSubmitValuesType<T, TTransformedValues>
              >,
            ) => (e?: React.BaseSyntheticEvent) => Promise<void>;
            void handleSubmit(onSubmit)();
          }
          event.preventDefault();
        }}
        {...props}>
        {children}
      </form>
    </FormProvider>
  );
};

export const RmFormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: ControllerProps<TFieldValues, TName>,
) => {
  return (
    <FormFieldContext.Provider value={{ name: props.name }}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  );
};

export const RmFormItem: FC<ComponentProps<"div">> = ({
  className,
  ...props
}) => {
  const id = useId();

  return (
    <FormItemContext.Provider value={{ id }}>
      <div data-slot="form-item" className={cn("grid", className)} {...props} />
    </FormItemContext.Provider>
  );
};

type RmLabelPropsType = ComponentProps<typeof RmLabel> & {
  isDisabled?: boolean;
  isRequired?: boolean;
};

export const RmFormLabel: FC<RmLabelPropsType> = ({
  className,
  isDisabled,
  isRequired,
  ...props
}) => {
  const { error, formItemId } = useFormField();

  return (
    <div className="flex">
      <RmLabel
        data-slot="form-label"
        data-error={Boolean(error)}
        className={cn(
          {
            "cursor-not-allowed opacity-50": isDisabled,
          },
          className,
        )}
        htmlFor={formItemId}
        {...props}
      />
      {isRequired && (
        <div className="mt-1 ml-1 h-1 w-1 rounded-full bg-destructive" />
      )}
    </div>
  );
};

export const RmFormControl: FC<ComponentProps<typeof Slot>> = props => {
  const { error, formItemId, formDescriptionId, formMessageId } =
    useFormField();

  return (
    <Slot
      data-slot="form-control"
      id={formItemId}
      aria-describedby={
        !error
          ? `${formDescriptionId}`
          : `${formDescriptionId} ${formMessageId}`
      }
      aria-invalid={Boolean(error)}
      {...props}
    />
  );
};

type RmFormDescriptionPropsType = ComponentProps<"p"> & ChildrenOrI18nType;

export const RmFormDescription: FC<RmFormDescriptionPropsType> = ({
  className,
  children,
  i18nKey,
  i18nOptions,
  ...props
}) => {
  const { formDescriptionId } = useFormField();
  const i18nText = useTranslate(i18nKey, i18nOptions);

  return (
    <p
      data-slot="form-description"
      id={formDescriptionId}
      className={cn(
        "whitespace-pre-line text-muted-foreground",
        textVariants(),
        className,
      )}
      {...props}>
      {children ?? i18nText}
    </p>
  );
};

type RmFormErrorMessagePropsType = ComponentProps<"p"> & {
  i18nKey?: I18nKeyType;
  i18nOptions?: object;
};

export const RmFormErrorMessage: FC<RmFormErrorMessagePropsType> = ({
  className,
  children,
  i18nKey,
  i18nOptions,
  ...props
}) => {
  const { error, formMessageId } = useFormField();
  const i18nText = useTranslate(i18nKey, i18nOptions);

  const errorMessage = error
    ? String(error?.message ?? error?.root?.message ?? "")
    : "";
  const body = error ? (i18nText ?? errorMessage) : children;

  const hasMessage = Boolean(body);

  return (
    <div
      className={cn(
        "min-h-0 overflow-hidden whitespace-pre-line transition-[height] duration-200",
        {
          "mt-2 min-h-5": hasMessage,
        },
      )}>
      <p
        data-slot="form-message"
        id={formMessageId}
        className={cn("text-destructive", textVariants(), className)}
        {...props}>
        {body}
      </p>
    </div>
  );
};
